/*
Copyright© Celligence International, LLC - 2024
This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained
access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.
*/

import { Alert, Button, Portal, Snackbar } from '@mui/material';
import { NumericFormat } from 'react-number-format';
import EnvelopeWithBox from '../EnvelopeWithBox';
import AmortizationScenarioCol from '../AmortizationScenarioCol';
import LoadingModal from '../modals/LoadingModal';
import Styles from '../../css/AmortizationSchedulePage.module.css';
import { isStrEmptyOrNull } from '../../common/UtilityFunctions';
import { useAuthContext } from '../../contexts/AuthContext';
import { useViewingContext } from '../../contexts/ViewingContext';
import { useAmortizationScheduleContext } from '../../contexts/AmortizationScheduleContext';

const AmortizationScheduleForm = () => {
  const { fullAccess } = useAuthContext();
  const { isServicerView } = useViewingContext();
  const amortizationScheduleContext = useAmortizationScheduleContext();

  return (
    <>
      <EnvelopeWithBox blockName={'Amortization Schedule'}>
        {amortizationScheduleContext.status === 'loading' && (
          <LoadingModal
            open={amortizationScheduleContext.status === 'loading'}
            loadingMessage={amortizationScheduleContext.loadingMessage}
          />
        )}
        <Portal>
          <Snackbar
            open={!!amortizationScheduleContext.statusAlert.message}
            onClose={() =>
              amortizationScheduleContext.setStatusAlert({
                status: '',
                message: '',
              })
            }
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            ClickAwayListenerProps={{ onClickAway: () => null }}
          >
            <Alert
              onClose={() =>
                amortizationScheduleContext.setStatusAlert({
                  status: '',
                  message: '',
                })
              }
              severity={amortizationScheduleContext.statusAlert.status as any}
              variant='filled'
              className={Styles.alert}
            >
              {amortizationScheduleContext.statusAlert.message}
            </Alert>
          </Snackbar>
        </Portal>
        <div className={Styles.amortTable}>
          <div className={Styles.leftColumn}>
            <table className={Styles.amortTableSection}>
              <tbody className={Styles.amortTableHeaderCol}>
                <tr className={Styles.amortTableHeaderRow}>
                  <th>&nbsp;</th>
                </tr>
                <tr>
                  <th>Due Date</th>
                </tr>
                <tr>
                  <th>Original Loan Amount</th>
                </tr>
                <tr>
                  <th>Original Loan Term</th>
                </tr>
                <tr>
                  <th>Principal Balance</th>
                </tr>
                <tr>
                  <th>Remaining Loan Term</th>
                </tr>
                <tr>
                  <th>Interest Rate</th>
                </tr>
                <tr>
                  <th>P & I Payment</th>
                </tr>
                <tr>
                  <th>Additional Principal</th>
                </tr>
              </tbody>
            </table>
          </div>
          <div className={Styles.rightColumn}>
            <table className={Styles.amortTableSection}>
              <tbody>
                <tr className={Styles.amortTableHeaderRow}>
                  <td>
                    {isStrEmptyOrNull(amortizationScheduleContext.servicerLoan)
                      ? amortizationScheduleContext.loanNumber
                      : amortizationScheduleContext.servicerLoan}
                  </td>
                </tr>
                <tr>
                  <td>{amortizationScheduleContext.dueDate.format('MM/DD/YYYY')}</td>
                </tr>
                <tr>
                  <td>
                    <NumericFormat
                      displayType='text'
                      value={amortizationScheduleContext.loanAmount}
                      decimalScale={2}
                      fixedDecimalScale
                      prefix='$'
                      thousandSeparator
                    />
                  </td>
                </tr>
                <tr>
                  <td>
                    <NumericFormat displayType='text' value={amortizationScheduleContext.loanTerm} thousandSeparator />
                  </td>
                </tr>
                <tr>
                  <td>
                    <NumericFormat
                      displayType='text'
                      value={amortizationScheduleContext.lnptData.upb}
                      decimalScale={2}
                      fixedDecimalScale
                      prefix='$'
                      thousandSeparator
                    />
                  </td>
                </tr>
                <tr>
                  <td>
                    <NumericFormat
                      displayType='text'
                      value={amortizationScheduleContext.lnptData.remainingTerm}
                      thousandSeparator
                    />
                  </td>
                </tr>
                <tr>
                  <td>
                    <NumericFormat
                      displayType='text'
                      value={amortizationScheduleContext.lnptData.intRate}
                      suffix='%'
                      thousandSeparator
                    />
                  </td>
                </tr>
                <tr>
                  <td>
                    <NumericFormat
                      displayType='text'
                      value={amortizationScheduleContext.lnptData.apparPi}
                      decimalScale={2}
                      fixedDecimalScale
                      prefix='$'
                      thousandSeparator
                    />
                  </td>
                </tr>
                <tr>
                  <td>
                    <NumericFormat
                      displayType='text'
                      value={amortizationScheduleContext.lnptData.prinCurtail}
                      decimalScale={2}
                      fixedDecimalScale
                      prefix='$'
                      thousandSeparator
                    />
                  </td>
                </tr>
                <tr>
                  <td className={Styles.tdButton}>
                    <Button
                      className={Styles.primaryButton}
                      disabled={!fullAccess && isServicerView}
                      onClick={() =>
                        amortizationScheduleContext.printAmortizationSchedule(amortizationScheduleContext.loanNumber)
                      }
                    >
                      Print Amortization
                    </Button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          {amortizationScheduleContext.scenarioIdList.current.includes('Z1') && (
            <AmortizationScenarioCol scenarioId={'Z1'} />
          )}
          {amortizationScheduleContext.scenarioIdList.current.includes('Z2') && (
            <AmortizationScenarioCol scenarioId={'Z2'} />
          )}
          {amortizationScheduleContext.scenarioIdList.current.includes('Z3') && (
            <AmortizationScenarioCol scenarioId={'Z3'} />
          )}
          {amortizationScheduleContext.scenarioIdList.current.includes('Z4') && (
            <AmortizationScenarioCol scenarioId={'Z4'} />
          )}
          {amortizationScheduleContext.scenarioIdList.current.includes('Z5') && (
            <AmortizationScenarioCol scenarioId={'Z5'} />
          )}
          {amortizationScheduleContext.scenarioIdList.current.includes('Z6') && (
            <AmortizationScenarioCol scenarioId={'Z6'} />
          )}
          {amortizationScheduleContext.scenarioIdList.current.includes('Z7') && (
            <AmortizationScenarioCol scenarioId={'Z7'} />
          )}
          {amortizationScheduleContext.scenarioIdList.current.includes('Z8') && (
            <AmortizationScenarioCol scenarioId={'Z8'} />
          )}
          {amortizationScheduleContext.scenarioIdList.current.includes('Z9') && (
            <AmortizationScenarioCol scenarioId={'Z9'} />
          )}
        </div>
      </EnvelopeWithBox>
      <div className={Styles.addScenarioDiv}>
        <Button
          className={Styles.primaryButton}
          disabled={!fullAccess && isServicerView}
          onClick={() => {
            amortizationScheduleContext.addAmortizationScenario();
          }}
        >
          + Add Scenario
        </Button>
        <Button
          className={Styles.primaryButton}
          disabled={!fullAccess && isServicerView}
          onClick={() => {
            amortizationScheduleContext.deleteAllScenarios();
          }}
        >
          Delete All Scenarios
        </Button>
      </div>
    </>
  );
};

export default AmortizationScheduleForm;
